import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ExpenseType {
  id: number
  name: string
  status: number
  usage_count: number
}

interface ExpenseHistoryRow {
  year: number
  totals: Record<string, number>
}

interface ExpenseHistory {
  types: string[]
  rows: ExpenseHistoryRow[]
}

interface Props {
  expense_types: ExpenseType[]
  expense_history: ExpenseHistory
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/expense_types" },
  { title: "Expense Types", href: "/admin/expense_types" },
]

function formatCurrency(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export default function Index({ expense_types, expense_history }: Props) {
  const handleDelete = (id: number, usageCount: number) => {
    const message =
      usageCount > 0
        ? "This type is in use. It will be deactivated instead of deleted. Continue?"
        : "Are you sure you want to delete this type?"

    if (confirm(message)) {
      router.delete(`/admin/expense_types/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Expense Types" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Expense Types</h1>
          <Link
            href="/admin/expense_types/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Type
          </Link>
        </div>

        {expense_types.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  ID
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  In Use
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {expense_types.map((et) => (
                <tr key={et.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.id}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.usage_count > 0 ? et.usage_count : ""}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/admin/expense_types/${et.id}/edit`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(et.id, et.usage_count)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No expense types found.</p>
        )}

        {expense_history.rows.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-4 text-xl font-semibold">Expense History</h2>
            <div className="overflow-x-auto">
              <table className="border-hpi-light-gray w-full border-collapse border">
                <thead>
                  <tr className="bg-hpi-pale-gray">
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Year
                    </th>
                    {expense_history.types.map((type) => (
                      <th
                        key={type}
                        className="border-hpi-light-gray max-w-[90px] overflow-hidden border px-3 py-2 text-right text-xs"
                      >
                        {type}
                      </th>
                    ))}
                    <th className="border-hpi-light-gray border px-4 py-2 text-right">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {expense_history.rows.map((row) => {
                    const rowTotal = Object.values(row.totals).reduce(
                      (sum, val) => sum + val,
                      0,
                    )
                    return (
                      <tr key={row.year} className="hover:bg-hpi-pale-blue">
                        <td className="bg-hpi-pale-gray border-hpi-light-gray border px-4 py-2 font-semibold">
                          {row.year}
                        </td>
                        {expense_history.types.map((type) => (
                          <td
                            key={type}
                            className="border-hpi-light-gray border px-3 py-2 text-right"
                          >
                            {row.totals[type]
                              ? formatCurrency(row.totals[type])
                              : ""}
                          </td>
                        ))}
                        <td className="border-hpi-light-gray border px-4 py-2 text-right font-bold">
                          {formatCurrency(rowTotal)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
